import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { TApi, TicketOpenStatus, TTicketOrigin, TTicketStatus } from '../../../../shared/const'
import { ticketListLink } from '../../../../shared/ticketListLink'
import { showTicketsMenu } from '../../../../shared/ticketsMenu'
import { isAlumni } from '../../../../shared/alumni'
import { stateFullLabel } from '../../../../shared/helpers'
import http from '../../../../shared/http'

import SectionHead from '../../../../UI/SectionHead'
import CounterWidget from '../../../../UI/Widget/counter'

import styles from './requestOverview.module.scss'

/*
 * Anforderungen auf dem Dashboard.
 *
 * Die Anforderungsverwaltung zeigt ihre Zahlen erst, wenn man sie öffnet: Reiter,
 * Trichter und Ring stehen alle **im** Menüpunkt. Wer den Tag auf dem Dashboard
 * beginnt, sieht dort Unternehmen, Schüler:innen und Rechnungen — und erfährt
 * nicht, dass seit gestern vier Meldungen unbearbeitet liegen.
 *
 * Deshalb hier dieselben Zahlen, aber nur die handlungsrelevanten, und jede als
 * Verweis auf genau die Liste dahinter. Eine Zahl ohne Weg dorthin wäre eine
 * Aufforderung, den Filter von Hand nachzubauen.
 *
 * Bewusst **keine** Kachel für „Abgeschlossen" und „Verworfen": Beides ist
 * erledigt und braucht keinen Platz auf einer Seite, die zeigen soll, was noch
 * offen ist. Wer die Zahlen sucht, findet sie im Trichter der Liste.
 *
 * **Die Kachel zählt offene Vorgänge — also führt sie auch in die Liste der
 * offenen.** Die Zuweisungs-Kacheln zählen quer über alle Stufen, was noch
 * offen ist; ohne die Stufen im Verweis stand hinter „Von Nutzer:innen: 0" eine
 * Liste mit sieben Zeilen. Eine Zahl, die etwas anderes zeigt als das, worauf
 * sie führt, ist schlimmer als keine Zahl.
 *
 * ---
 *
 * **Zwei Gruppen statt acht Kacheln in einer Reihe** (seit 26.08.2026).
 *
 * Acht gleich aussehende Kästen nebeneinander beantworten die erste Frage
 * nicht, die jemand an eine Startseite hat: *Was davon geht mich an?* Besonders
 * für Landesadministrator:innen — deren Zahlen unter „Neu" oder „In Arbeit"
 * sind **bundesweite** Zahlen und beschreiben Arbeit, die anderswo geleistet
 * wird. Wer das nicht weiß, liest sie als eigene Rückstände.
 *
 * Deshalb: oben, was bei der angemeldeten Person liegt; darunter, was im
 * Bestand steht. Jede Gruppe sagt in einem Satz, was sie ist. Verschwiegen wird
 * nichts — die Zahlen stehen alle noch da, nur nicht mehr alle als Aufgabe.
 */

// Was der Server je Statusstufe zählt. Wortlaut wie in der Liste
// (TicketStatusOptions) — zwei Wörter für dieselbe Stufe wären zwei Stufen für
// die Lesenden.
const STUFEN = {
	[TTicketStatus.NEW]: { label: 'Neu', hint: 'Noch nicht eingeordnet' },
	[TTicketStatus.CLARIFICATION]: { label: 'Klärung offen', hint: 'Rückfrage läuft' },
	[TTicketStatus.READY]: { label: 'Eingeplant', hint: 'Wartet auf den Start' },
	[TTicketStatus.IN_DEVELOPMENT]: { label: 'In Arbeit', hint: 'Wird gerade bearbeitet' },
	[TTicketStatus.REVIEW]: { label: 'Zur Bestätigung', hint: 'Wartet auf die Abnahme' }
}

const stufenKachel = status => ({
	key: status,
	label: STUFEN[status].label,
	hint: STUFEN[status].hint,
	zahl: counts => counts.byStatus?.[status] || 0,
	to: ticketListLink({ status })
})

/*
 * Die Kacheln, die nach der Zuweisung fragen statt nach der Stufe: nicht „wo
 * steht der Bestand?", sondern „was liegt bei mir?".
 */
const MIR_ZUGEWIESEN = {
	key: 'assignedToMe',
	label: 'Mir zugewiesen',
	hint: 'Offen, an mich vergeben',
	zahl: counts => counts.assignedToMe || 0,
	to: ticketListLink({ scope: 'assignedToMe', status: TicketOpenStatus })
}

/*
 * Wartet draußen jemand auf Antwort? Eine intern notierte Anforderung und die
 * Meldung einer Schülerin stehen in derselben Liste — nur bei der zweiten sitzt
 * ein Mensch davor, der auf eine Rückmeldung wartet. Die Aufschlüsselung nach
 * Rolle steht in der Liste selbst.
 */
const VON_NUTZERINNEN = {
	key: 'enduser',
	label: 'Von Nutzer:innen',
	hint: 'Offen, über den Hilfe-Knopf gemeldet',
	zahl: counts => counts.enduser || 0,
	to: ticketListLink({ herkunft: TTicketOrigin.ENDUSER, status: TicketOpenStatus })
}

const meinBundesland = land => ({
	key: 'myState',
	label: `Für ${stateFullLabel(land) || land}`,
	hint: 'Offen, Ihrem Bundesland zugeordnet',
	zahl: counts => counts.myState || 0,
	to: ticketListLink({ scope: 'myState', status: TicketOpenStatus })
})

/**
 * Welche Kacheln in welcher Gruppe stehen — und warum.
 *
 * Landesadministration und Bundesebene bekommen **dieselben** Kacheln, nur
 * anders geordnet: Was für die eine Rolle eine Aufgabe ist, ist für die andere
 * eine Randnotiz. „Neu — noch nicht eingeordnet" etwa wird von der Bundesebene
 * abgearbeitet; in einem Bundesland ist es eine Information darüber, wie voll
 * der Eingang gerade ist.
 *
 * Unterschieden wird am **eigenen Bundesland** und nicht an der Rolle: Genau
 * das ist der Unterschied zwischen den beiden Verwaltungsrollen — die
 * Bundesebene hat keines. Dieselbe Bedingung wie am Reiter der Liste
 * (views/admin/Tickets).
 */
const gruppenFuer = eigenesLand => {
	if (eigenesLand) {
		return [
			{
				key: 'meins',
				titel: 'Das liegt bei Ihnen',
				satz: 'Vorgänge, für die Sie zuständig sind. Hier wird auf Sie gewartet.',
				handlungsbedarf: true,
				kacheln: [meinBundesland(eigenesLand), MIR_ZUGEWIESEN]
			},
			{
				key: 'bestand',
				titel: 'Zur Information — bundesweit',
				satz: 'Der Stand aller Bundesländer zusammen. Diese Vorgänge bearbeitet Junior Achievement Austria; für Sie ist hier nichts zu tun.',
				handlungsbedarf: false,
				kacheln: [
					VON_NUTZERINNEN,
					stufenKachel(TTicketStatus.NEW),
					stufenKachel(TTicketStatus.CLARIFICATION),
					stufenKachel(TTicketStatus.READY),
					stufenKachel(TTicketStatus.IN_DEVELOPMENT),
					stufenKachel(TTicketStatus.REVIEW)
				]
			}
		]
	}

	return [
		{
			key: 'zutun',
			titel: 'Das steht an',
			satz: 'Vorgänge, die auf eine Entscheidung oder eine Antwort warten.',
			handlungsbedarf: true,
			kacheln: [
				stufenKachel(TTicketStatus.NEW),
				stufenKachel(TTicketStatus.CLARIFICATION),
				stufenKachel(TTicketStatus.REVIEW),
				MIR_ZUGEWIESEN,
				VON_NUTZERINNEN
			]
		},
		{
			key: 'laeuft',
			titel: 'Zur Information — läuft bereits',
			satz: 'Eingeplant oder in Bearbeitung. Hier ist nichts zu tun.',
			handlungsbedarf: false,
			kacheln: [stufenKachel(TTicketStatus.READY), stufenKachel(TTicketStatus.IN_DEVELOPMENT)]
		}
	]
}

export default function RequestOverview() {
	const [counts, setCounts] = useState(null)
	const [fehler, setFehler] = useState(false)

	/*
	 * Ist der Menuepunkt ausgeblendet (auf der Testumgebung die Vorgabe), hat
	 * der Abschnitt hier nichts zu suchen: Er zaehlt genau die Vorgaenge, die
	 * dort nicht bearbeitet werden, und jede seiner Kacheln fuehrt in eine
	 * Liste, zu der es keinen Menuepunkt mehr gibt.
	 */
	// Alumni (#213) haben keinen Zugang zu Anforderungen & Anfragen — der
	// Abschnitt fehlt, und es wird auch nicht gezählt.
	const sichtbar = useSelector(state => showTicketsMenu(state.options) && !isAlumni(state.user?.role))

	// „An mein Bundesland" hat nur mit eigenem Bundesland eine Bedeutung —
	// dieselbe Bedingung wie am Reiter der Liste (views/admin/Tickets).
	const eigenesLand = useSelector(state => state.user.profile?.state)

	useEffect(() => {
		// Ist der Abschnitt ausgeblendet, wird auch nicht gezaehlt.
		if (!sichtbar) return

		// `disableToast`: Eine fehlgeschlagene Nebenrechnung des Dashboards ist
		// keine Meldung wert, die man wegklicken muss — der Abschnitt sagt es
		// selbst. Leere Kacheln ohne Hinweis wären dagegen eine Falschauskunft.
		http.get(TApi.ADMIN_TICKET_COUNTS, { disableToast: true })
			.then(({ data }) => setCounts(data))
			.catch(() => setFehler(true))
	}, [sichtbar])

	const gruppen = gruppenFuer(eigenesLand)

	if (!sichtbar) return null

	return (
		<section className={styles.root}>
			<SectionHead
				title="Anforderungen & Anfragen"
				count={counts?.all}
				linkTo={ticketListLink()}
				linkLabel="Zur Übersicht"
			/>

			{fehler ? (
				<p className={styles.fehler}>
					Die Anforderungen und Anfragen konnten nicht geladen werden. Die Zahlen unten fehlen deshalb — die
					Liste selbst ist über „Zur Übersicht" erreichbar.
				</p>
			) : (
				gruppen.map(gruppe => (
					<div className={styles.gruppe} key={gruppe.key}>
						<h4 className={styles.gruppeTitel}>{gruppe.titel}</h4>
						<p className={styles.gruppeSatz}>{gruppe.satz}</p>
						<div className={styles.kacheln}>
							{gruppe.kacheln.map(kachel => (
								<CounterWidget
									key={kachel.key}
									className={styles.kachel}
									label={kachel.label}
									hint={kachel.hint}
									count={counts ? kachel.zahl(counts) : 0}
									/*
									 * Orange nur in der oberen Gruppe: Wenn alles
									 * leuchtet, leuchtet nichts. Die untere Gruppe
									 * ist ausdrücklich nichts zu tun — sie bekommt
									 * die ruhige Fassung derselben Kachel.
									 */
									attention={gruppe.handlungsbedarf}
									to={kachel.to}
								/>
							))}
						</div>
					</div>
				))
			)}
		</section>
	)
}
